import { useCallback } from 'react';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { CreateEventFeature } from '@shared/constants/customPageMigration';
import { extractMigratedPageFeatures } from '@shared/constants/customPageMigration';

/**
 * Returns a function that computes the eventFeatures array based on
 * currently enabled feature flags. These features are passed to the
 * backend during event creation to configure the new event.
 */
export const useCreateEventFeatures = () => {
  const [isWebsiteDesignerV3Enabled] = useIsFeatureEnabled('websiteDesignerV3Enabled');
  const { value: websiteAllPagesAsCustomPagesValue, payload: websiteAllPagesAsCustomPagesPayload } = useFeatureValue('websiteAllPagesAsCustomPagesEnabled');
  const isWebsiteAllPagesAsCustomPagesEnabled = websiteAllPagesAsCustomPagesValue === 'on' || websiteAllPagesAsCustomPagesValue === 'onjoysuperuser';
  const [isGuestPageContentRedesignEnabled] = useIsFeatureEnabled('guestPageContentRedesign');
  const [isSunsetVideoPageEnabled] = useIsFeatureEnabled('sunsetVideoPageEnabled');
  const [isUnifiedWebsiteEditorExperienceEnabled] = useIsFeatureEnabled('unifiedWebsiteEditorExperience');

  const getEventFeatures = useCallback((): ReadonlyArray<CreateEventFeature> => {
    // Theme customization restrictions will be removed for all newly created events.
    const features: Array<CreateEventFeature> = ['bypassWebsiteThemeCustomization'];

    if (isWebsiteDesignerV3Enabled) {
      features.push('useWebsiteDesignerV3');
    }

    // Only derive per-page event features when the top-level remote flag is enabled.
    if (isWebsiteAllPagesAsCustomPagesEnabled) {
      features.push(...extractMigratedPageFeatures(websiteAllPagesAsCustomPagesPayload, 'migratedPages'));
    }

    if (isGuestPageContentRedesignEnabled) {
      features.push('useGuestPageContentRedesign');
    }

    // Sunset (PRNT-4148): persist a per-event flag at create time so the legacy Video page
    // is permanently hidden for new events. Decision is captured here and never re-evaluated.
    if (isSunsetVideoPageEnabled) {
      features.push('sunsetVideoPageEnabled');
    }

    if (isUnifiedWebsiteEditorExperienceEnabled) {
      features.push('useUnifiedWebsiteEditor');
    }

    return features;
  }, [
    isWebsiteDesignerV3Enabled,
    isWebsiteAllPagesAsCustomPagesEnabled,
    isGuestPageContentRedesignEnabled,
    isSunsetVideoPageEnabled,
    isUnifiedWebsiteEditorExperienceEnabled,
    websiteAllPagesAsCustomPagesPayload,
  ]);

  return { getEventFeatures };
};
